import {
    type ComplaintDraftSource,
    type FormDraftSource,
    type FormDraftSourceType,
    type IncidentDraftSource,
    MAX_BIGINT_ID,
} from '@shared/constants/system/org-flex-storage';
import { z } from 'zod';

/**
 * CUR-1825: the record an incident/complaint create form was opened from, as the
 * draft procedures accept it. Bounded to a positive PostgreSQL `bigint`, so every
 * accepted source round-trips through `buildFormDraftDataType` /
 * `parseFormDraftDataType`.
 */
const formDraftSourceVariant = <T extends FormDraftSourceType>(type: T) =>
    z
        .object({
            type: z.literal(type),
            id: z.bigint().min(1n).max(MAX_BIGINT_ID),
        })
        .strict();

const complianceAuditFindingSource = formDraftSourceVariant('complianceAuditFinding');
const messageClassificationSource = formDraftSourceVariant('messageClassification');
const complaintSource = formDraftSourceVariant('complaint');
const incidentSource = formDraftSourceVariant('incident');
const claimSource = formDraftSourceVariant('claim');

export const formDraftSourceSchema = z.discriminatedUnion('type', [
    complianceAuditFindingSource,
    messageClassificationSource,
    complaintSource,
    incidentSource,
    claimSource,
]) satisfies z.ZodType<FormDraftSource>;

/** Mirrors `INCIDENT_DRAFT_SOURCE_TYPES`: a claim or incident source is refused. */
export const incidentDraftSourceSchema = z.discriminatedUnion('type', [
    complianceAuditFindingSource,
    messageClassificationSource,
    complaintSource,
]) satisfies z.ZodType<IncidentDraftSource>;

/** Mirrors `COMPLAINT_DRAFT_SOURCE_TYPES`: a complaint source is refused. */
export const complaintDraftSourceSchema = z.discriminatedUnion('type', [
    complianceAuditFindingSource,
    messageClassificationSource,
    incidentSource,
    claimSource,
]) satisfies z.ZodType<ComplaintDraftSource>;
